/**
 * /employers/signup — focused conversion page. No long landing copy: just
 * the JobIntakeWidget + benefits sidebar + trust bar. On successful intake
 * we bounce to /thank-you-employer so conversion pixels (Meta / Google Ads)
 * fire once with the right event.
 */
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldCheck, Zap, Users, Sparkles } from "lucide-react";
import { JobIntakeWidget } from "@/components/employer/JobIntakeWidget";
import { SocialProof } from "@/components/marketing/SocialProof";

const BENEFITS = [
    { icon: Zap, title: "First shortlist in hours", body: "Upload a role and get five AI-matched verified candidates before your next standup." },
    { icon: ShieldCheck, title: "Every candidate pre-verified", body: "Identity, email, phone, employment, and references — done before you meet them." },
    { icon: Users, title: "No agency fees, no per-hire", body: "Flat monthly plans. Post as many roles as you need. Hire without paying 20% of first-year comp." },
    { icon: Sparkles, title: "AI Hiring Brief on every match", body: "Executive-ready one-pager explaining why each candidate fits — no more resume forensics." },
];

export default function EmployerSignup() {
    const navigate = useNavigate();

    const handleSuccess = (data) => {
        // Persist claim token so the /thank-you page can offer the "finish setup" CTA.
        try {
            if (data?.intake_id && data?.claim_token) {
                localStorage.setItem("simonara_pending_intake", JSON.stringify({
                    intake_id: data.intake_id,
                    claim_token: data.claim_token,
                }));
            }
        } catch { /* ignore quota errors */ }
        navigate("/thank-you-employer");
    };

    return (
        <div className="bg-background" data-testid="employer-signup-page">
            <Helmet>
                <title>Post a Job · Simonara Talent Solutions</title>
                <meta name="description" content="Post your first role and receive five AI-matched, pre-verified Talent Passports today. No agency fees. Flat pricing." />
                <link rel="canonical" href="/employers/signup" />
            </Helmet>

            <section className="container-page pt-14 pb-8">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="max-w-2xl"
                >
                    <p className="overline">Get started · Employers</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-foreground">
                        Post your first role. See five matched Talent Passports today.
                    </h1>
                    <p className="mt-3 text-muted-foreground">
                        Paste a URL, drop a PDF, or type the description. We handle the rest —
                        parsing, matching, and delivering an executive-ready shortlist.
                    </p>
                </motion.div>
            </section>

            <section className="container-page pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        {/* Wrap in a marker div so the testing agent can key off the form root */}
                        <div data-testid="employer-signup-form" data-conversion="employer_signup_form">
                            <JobIntakeWidget onSuccess={handleSuccess} />
                            <p className="mt-3 text-xs text-muted-foreground">
                                By submitting you agree to our{" "}
                                <Link to="/terms" className="underline">Terms</Link> and{" "}
                                <Link to="/privacy" className="underline">Privacy Policy</Link>.
                            </p>
                        </div>
                    </div>
                    <aside className="lg:col-span-5 order-1 lg:order-2">
                        <div className="card-soft p-6 sticky top-24">
                            <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-600 dark:text-emerald-400">
                                What you get
                            </div>
                            <ul className="mt-4 space-y-4">
                                {BENEFITS.map((b) => (
                                    <li key={b.title} className="flex gap-3">
                                        <div className="mt-0.5 w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                            <b.icon size={16} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-foreground">{b.title}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{b.body}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-foreground inline-flex items-start gap-2">
                                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                                <span>Free to post. You only pay when you're ready to unlock a candidate's full profile.</span>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            <SocialProof compact />
        </div>
    );
}
